import React from 'react';
import { find, get, isEmpty, keys } from 'lodash';
import { extractCommentIds } from './comment-utils';
import { FormLabel, FormControl, FormGroup } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import CommentForm from '../../BaseCommentForm';
import { getSectionCommentingMessage } from '../../../utils/section';
import getMessage from '../../../utils/getMessage';
import { getCommentIds } from '../../../store/actions';

export const ORDERING_CRITERIA = {
  CREATED_AT_DESC: '-created_at',
  CREATED_AT_ASC: 'created_at',
};

export const DEFAULT_ORDERING = ORDERING_CRITERIA.CREATED_AT_ASC;

export const COMMENT_POLL_INTERVAL_IN_MS = 1000 * 10;

const useSortableCommentList = () => {

  const showCommentList = (section, sectionComments) =>
    section &&
    sectionComments &&
    get(sectionComments, 'lists') &&
    !isEmpty([...sectionComments.lists.pinned, ...sectionComments.lists.base]);


  const renderOrderingComponent = (setOrdering, handleFetchComments, sectionOrdering, sectionId, sectionComments) =>
    (
      <div>
        <form className="sort-selector">
            <label htmlFor="comment-sort-select-input">
              <FormattedMessage id="commentOrder" />
            </label>
            <select 
              id="comment-sort-select-input"
              className="comment-sort-select" 
              value={sectionOrdering}
              onChange={event => {
                setOrdering(event.target.value);
                handleFetchComments(sectionId, event.target.value);
              }}
            >
              {keys(ORDERING_CRITERIA).map(key => (
                <FormattedMessage
                  id={key}
                  key={key}
                >
                  {message => (
                    <option value={ORDERING_CRITERIA[key]}>
                      {message}
                    </option>
                  )}
                </FormattedMessage>
              ))}
            </select>
        </form>
      </div>
    );

  const pluginContent = (section, sectionComments, displayVisualization) =>
    showCommentList(section, sectionComments) && displayVisualization
      ? renderPluginContent(section, sectionComments)
      : null;

  const defaultAnswerState = (section) => {
    return section.questions.map(question => ({
      question: question.id,
      type: question.type,
      answers: [],
    }));
  };

  /*
  Returns list of all comments under section, including replies to comments.
  If results are not fetched yet return undefined
 */
  const getAllLoadedComments = (sectionComments) => {
    if (!!sectionComments?.isFetching) {
      return undefined;
    }
    return Object.keys(sectionComments?.commentData ?? {}).map(c => c.id)
  };

  /**
   * When posting a new comment.
   */
  const handlePostReplyComment = (
    section, sectionCommentId, commentData, onPostComment, setAnswers, removeEditingReply,
  ) => {
    if (onPostComment) {
      onPostComment(section.id, sectionCommentId, commentData).then(() => {
        setAnswers(defaultAnswerState(section.id));
        removeEditingReply(commentData.comment, section.id);
      });
    }
  };

  const onChangeAnswers = (questionId, questionType, value, answers, setAnswers) => {
    const oldAnswer = find(
      answers,
      answer => answer.question === questionId,
    );
    if (questionType === 'single-choice') {
      setAnswers((prevState) => {
        return [
          ...prevState.filter(
            answer => answer.question !== questionId,
          ),
          {
            question: questionId,
            type: questionType,
            answers: [value],
          },
        ];
      });
    } else if (questionType === 'multiple-choice' && oldAnswer?.answers.includes(value)) {
      setAnswers((prevState) => {
        return [
          ...prevState.filter(
            answer => answer.question !== questionId,
          ),
          {
            ...oldAnswer,
            answers: oldAnswer.answers.filter(answer => answer !== value),
          },
        ];
      });
    } else if (questionType === 'multiple-choice' && oldAnswer) {

      setAnswers((prevState) => {
        return [
          ...prevState.filter(
            answer => answer.question !== questionId,
          ),
          {
            ...oldAnswer,
            answers: [...oldAnswer.answers, value],
          },
        ];
      });
    }
  };

  const renderCommentForm = (
    canComment,
    hearingId,
    handlePostNewSectionComment,
    user,
    collapseForm,
    section,
    language,
    answers,
    setAnswers,
    closed,
    hearingGeojson,
    setEditingNewSectionComment,
    newSectionCommentEditingText,
    cancelNewSectionComment,
  ) => (
      <div
        className={classnames('comment-form-container', {
          disabled: !canComment,
        })}
      >
        <CommentForm
          canComment={canComment}
          hearingId={hearingId}
          onPostComment={handlePostNewSectionComment}
          defaultNickname={user.username}
          nicknamePlaceholder={
            user.username ||
            getMessage('anonymous')
          }
          collapseForm={collapseForm}
          section={section}
          language={language}
          onChangeAnswers={(questionId, questionType, value) => onChangeAnswers(questionId, questionType, value, answers, setAnswers)}
          answers={answers}
          closed={closed}
          loggedIn={!isEmpty(user)}
          user={user}
          hearingGeojson={hearingGeojson}
          commentsCount={section.n_comments}
          setEditing={(value) => setEditingNewSectionComment(section.id, value)}
          editedText={newSectionCommentEditingText}
          cancelEditing={cancelNewSectionComment}
          hasDelayToSaveType={true}
        />
        {!canComment && (
          <FormattedMessage id={getSectionCommentingMessage(section)} />
        )}
      </div>
  );

  const amountOfUnloadedComments = (response, sectionComments) => {
    const allComments = getAllLoadedComments(sectionComments);
    let _amountOfNewComments = 0;
    for (let i = 0; i < response.length; i++) {
      const minimalCommentDTO = response[i];
      if (commentIsNewComment(minimalCommentDTO, allComments)) {
        _amountOfNewComments += 1;
      }
    }
    return _amountOfNewComments;
  };

  const checkForMoreComments = (
    sectionComments,
    hearingId,
    sectionId,
    amountOfNewComments,
    setTempNewUnreadComments,
    setNewFocusCommentId,
    setAmountOfNewComments
  ) => {
    if (sectionComments?.isFetching) {
      return;
    }
    const lastFetchTime = sectionComments?.fetchTime ?? new Date();
    getCommentIds({}, hearingId, sectionId, lastFetchTime, 'False')
      .then(commentsSinceLastFetch => {
        // This is basically double check. if lastFetchTime is in same second there might be some comments already loaded which show up already in UI
        // backend or UI-clock might not be synced to the millisecond
        const _amountOfNewComments = amountOfUnloadedComments(commentsSinceLastFetch, sectionComments);
        const latestComment = commentsSinceLastFetch?.length ? commentsSinceLastFetch.reduce((r, a) => r.created_at > a.created_at ? r : a) : null;
        if (_amountOfNewComments > 0 && _amountOfNewComments > amountOfNewComments) {
          setTempNewUnreadComments(commentsSinceLastFetch)
          setNewFocusCommentId(latestComment.id);
          setAmountOfNewComments(_amountOfNewComments);
        }
      });
  };

  const handleNewUnreadComments=(newUnreadComments, setNewCommentChainMap)=>{
    const tempMap = new Map();
    newUnreadComments.forEach(c => {
      if (!tempMap.has(c.section_comment_id)) {
        tempMap.set(c.section_comment_id,
          {
            chainToParent: [],
            newCommentIds: [],
          });
      }
      const oldObj = tempMap.get(c.section_comment_id);
      oldObj.chainToParent.push(...c.chain_to_parent.filter(i => i !== c.id && !oldObj.chainToParent.includes(i)));
      oldObj.newCommentIds.push(c.id);
    });
    setNewCommentChainMap(tempMap);
  }


  const commentIsNewComment = (minimalCommentDTO, idsOfLoadedComments) => {
    // If we don't know the ids, comment is not new by default
    if (idsOfLoadedComments === undefined) {
      return false;
    }
    return !idsOfLoadedComments.includes(minimalCommentDTO.id);
  };

  return {
    pluginContent,
    showCommentList,
    defaultAnswerState,
    handlePostReplyComment,
    onChangeAnswers,
    renderOrderingComponent,
    renderCommentForm,
    checkForMoreComments,
    handleNewUnreadComments,
  };

};

export default useSortableCommentList;